import * as Api from '@ViewModels';
import { action, computed, makeObservable, observable, override } from 'mobx';
import { GameViewModel } from './games';

export abstract class AchievementsViewModel<
	TRewardConfig extends Api.IAchievementRewardConfiguration = Api.IAchievementRewardConfiguration,
	TAchievementConfig extends
		Api.IAchievementConfiguration<TRewardConfig> = Api.IAchievementConfiguration<TRewardConfig>,
	TAchievement extends AchievementViewModel<TRewardConfig> = AchievementViewModel<TRewardConfig>,
> extends Api.ViewModel {
	protected mAchievements: TAchievement[];

	constructor(userSession: Api.UserSessionContext) {
		super(userSession);

		makeObservable<AchievementsViewModel<TRewardConfig, TAchievementConfig, TAchievement>, 'mAchievements'>(this, {
			mAchievements: observable.ref,
			fetchResults: computed,
			load: override,
		});

		this.createAchievement = this.createAchievement.bind(this);
	}

	public get fetchResults() {
		return this.mAchievements;
	}

	public load = async () => {
		if (this.loading) {
			return;
		}

		this.loading = true;
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<
			Api.IAchievement<TRewardConfig, TAchievementConfig>[]
		>(this.composeApiUrl({ urlPath: 'achievement' }), 'GET');

		this.loading = false;

		if (!opResult.success) {
			throw opResult;
		}

		const achievements = opResult.value.map(x => this.createAchievement(x));
		await Promise.all(achievements.map(x => x.load()));
		this.mAchievements = achievements;
		this.loaded = true;
		return opResult;
	};

	protected abstract createAchievement(model: Api.IAchievement<TRewardConfig, TAchievementConfig>): TAchievement;
}

export class AchievementViewModel<
	TRewardConfig extends Api.IAchievementRewardConfiguration = Api.IAchievementRewardConfiguration,
	TGameType extends Api.IGame = Api.IGame,
	TGame extends GameViewModel<TGameType> = GameViewModel<TGameType>,
> extends Api.ViewModel {
	protected mAchievement: Api.IAchievement<TRewardConfig>;
	protected mGame: TGame;

	constructor(userSession: Api.UserSessionContext, achievement: Api.IAchievement<TRewardConfig>) {
		super(userSession);

		makeObservable<AchievementViewModel<TRewardConfig, TGameType>, 'mAchievement' | 'mGame'>(this, {
			mAchievement: observable.ref,
			mGame: observable.ref,
			configuration: computed,
			description: computed,
			type: computed,
			load: override,
			setConfiguration: action,
		});

		this.mAchievement = achievement;
		this.canSaveConfiguration = this.canSaveConfiguration.bind(this);
		this.mSetConfiguration = this.mSetConfiguration.bind(this);
	}

	public get configuration() {
		return this.mAchievement?.achievementConfiguration;
	}

	public get description() {
		return this.mAchievement?.description;
	}

	public get type() {
		return this.mAchievement?.type;
	}

	public load = async () => {
		if (this.mGame?.isLoaded) {
			return;
		}

		let gameId: string = null;
		switch (this.mAchievement?.achievementConfiguration?.rewardConfiguration.rewardType) {
			case Api.AchievementRewardType.GameToken: {
				const config = this.mAchievement.achievementConfiguration
					.rewardConfiguration as any as Api.IGameTokenRewardConfiguration;
				gameId = config.gameId;
				break;
			}
			default: {
				break;
			}
		}

		if (!gameId) {
			return;
		}

		if (this.loading) {
			return;
		}

		this.loading = true;

		const game = new GameViewModel<TGameType>(this.mUserSession, { id: gameId } as TGameType) as TGame;
		const opResult = await game.load();

		this.loading = false;

		if (!opResult.success) {
			throw opResult;
		}

		this.loaded = true;
		this.mGame = game;
	};

	public setConfiguration = async (config: Partial<Api.IAchievementConfiguration<TRewardConfig>>) => {
		if (this.busy) {
			return;
		}

		this.busy = true;
		const opResult = await this.mSetConfiguration(config);
		this.busy = false;
		if (!opResult.success) {
			throw opResult;
		}

		return opResult;
	};

	public canSaveConfiguration(config: Api.IAchievementConfiguration<TRewardConfig>) {
		return !!config?._type && !!config?.rewardConfiguration;
	}

	protected async mSetConfiguration(config: Partial<Api.IAchievementConfiguration<TRewardConfig>>) {
		const create = !this.mAchievement?.achievementConfiguration?.id;
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<
			Api.IAchievementConfiguration<TRewardConfig>
		>(
			this.composeApiUrl({ urlPath: `achievement/configuration${create ? '' : `/${config.id}`}` }),
			create ? 'POST' : 'PUT',
			config._type
				? config
				: {
						...config,
						_type: 'AchievementConfiguration',
					}
		);

		if (opResult.success) {
			this.mAchievement = {
				...this.mAchievement,

				achievementConfiguration: opResult.value,
			};
		}

		return opResult;
	}
}
